import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { z } from "zod";
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import {
  getLearningGetCourseDetailQueryOptions,
  getLearningGetCourseModulesQueryOptions,
} from "@/services/api/learning/learning";
import {
  getGetCourseProgressQueryKey,
  getGetCourseProgressQueryOptions,
} from "@/services/api/learning-progress/learning-progress";
import { getListUserEnrollmentsQueryKey } from "@/services/api/learning-enrollments/learning-enrollments";
import { queryClient } from "@/services/queryClient";
import { PlayerHeader } from "@/features/learning/components/PlayerHeader";
import { ModuleNavigationSidebar } from "@/features/learning/components/ModuleNavigationSidebar";
import { useModuleNavigation } from "@/features/learning/hooks/useModuleNavigation";
import { useCoursePlayer } from "@/features/learning/hooks/useCoursePlayer";
import { ScormApiProvider, useScormApi } from "@/features/learning/scorm/ScormApiProvider";
import { ScormPlayer } from "@/features/learning/scorm/ScormPlayer";
import { normalizeAdaptMetric, parseAdaptSuspendData } from "@/features/learning/scorm/adaptUtils";
import type {
  AdaptSuspendData,
  ScormProgressEvent,
  ScormSessionConfig,
} from "@/features/learning/scorm/types";
import { ScormStructureType } from "@/features/admin/types";
import { toNum } from "@/utils/course-formatters";
import { CourseProgressBar } from "@/features/learning/components/CourseProgressBar";
import { ShareCourseButton } from "@/features/learning/components/ShareCourseButton";
import { CompletionModal } from "@/features/learning/components/CompletionModal";
import { CourseEvaluationModal } from "@/features/learning/components/CourseEvaluationModal";
import { useScormProgress } from "@/features/learning/hooks/useScormProgress";
import { PROGRESS_STATUS } from "@/utils/progressStatus";
import { downloadCourseCertificate } from "@/services/certificate-client";
import {
  computeShouldRenderSidebar,
  getModuleNavigationRules,
  INTERNAL_NAVIGATION_REASON,
} from "@/features/learning/utils/sidebarVisibility";
import { createHandleExit } from "@/features/learning/utils/createHandleExit";
import { resetCatalogHighlights } from "@/features/catalog/hooks/useCatalogHighlights";
import { invalidateLearningPathProgress } from "@/features/learning-paths/utils/invalidateLearningPathProgress";
import {
  ensurePlayerCourseDetail,
  invalidateCourseDetails,
} from "@/features/learning/utils/courseAccess";

/** Bridges forceCommit from ScormApiProvider context to a parent ref. */
function ScormCommitBridge({
  commitRef,
}: {
  commitRef: MutableRefObject<(() => Promise<void>) | null>;
}) {
  const { forceCommit } = useScormApi();
  useEffect(() => {
    commitRef.current = forceCommit;
    return () => {
      commitRef.current = null;
    };
  }, [commitRef, forceCommit]);
  return null;
}

const playerSearchSchema = z.object({
  moduleId: z.string().optional(),
});

type PlayerSearch = z.infer<typeof playerSearchSchema>;

const updateModuleSearch =
  (nextModuleId: string) =>
  (prev: PlayerSearch | undefined): PlayerSearch => ({
    ...(prev ?? {}),
    moduleId: nextModuleId,
  });

export const Route = createFileRoute("/learning/courses/$courseId/player")({
  validateSearch: playerSearchSchema,

  beforeLoad: async ({ location }) => {
    const { isAuthenticated } = useAuthStore.getState();

    if (!isAuthenticated) {
      throw redirect({
        to: "/login",
        search: {
          redirect: location.href,
        },
      });
    }
  },

  loader: async ({ params: { courseId } }) => {
    // Fetch course details
    const coursePromise = ensurePlayerCourseDetail(queryClient, courseId);

    // Fetch course modules
    const modulesOptions = getLearningGetCourseModulesQueryOptions(courseId);
    const modulesPromise = queryClient.ensureQueryData(modulesOptions);

    // Fetch course progress
    const progressOptions = getGetCourseProgressQueryOptions(courseId);
    const progressPromise = queryClient.ensureQueryData(progressOptions);

    // Wait for all data
    const [course, modules, progress] = await Promise.all([
      coursePromise,
      modulesPromise,
      progressPromise,
    ]);

    // Paid-course gate: a learner without an entitlement must not reach the player. The server also
    // blocks launch with 403, but redirect to the course detail page so they see the locked/price
    // state instead of a generic SCORM initialization error.
    if (course?.isPaid && !course.isEntitled) {
      throw redirect({
        to: "/courses/$courseId",
        params: { courseId },
      });
    }

    return { course, modules, progress };
  },

  component: CoursePlayerPage,
});

function CoursePlayerPage() {
  const navigate = useNavigate({ from: "/learning/courses/$courseId/player" });
  const { courseId } = Route.useParams();
  const { moduleId } = Route.useSearch();

  // Fetch data with suspense
  const courseOptions = getLearningGetCourseDetailQueryOptions(courseId);
  const { data: course } = useSuspenseQuery({
    ...courseOptions,
    queryFn: typeof courseOptions.queryFn === "function" ? courseOptions.queryFn : undefined!,
  });
  const isAdaptStructure = course?.structureType === ScormStructureType.SingleScoAdaptLike;
  const { showModuleNavigation, lockingDisabled, hasInternalNav, canInteractWithSidebar } =
    getModuleNavigationRules({
      moduleNavigationEnabled: course?.moduleNavigationEnabled,
      moduleLockingEnabled: course?.moduleLockingEnabled,
      isAdaptStructure,
    });

  const modulesOptions = getLearningGetCourseModulesQueryOptions(courseId);
  const { data: modules } = useSuspenseQuery({
    ...modulesOptions,
    queryFn: typeof modulesOptions.queryFn === "function" ? modulesOptions.queryFn : undefined!,
  });

  // Use useScormProgress hook for polling
  const { data: courseProgress } = useScormProgress(courseId);
  const courseVersionId = courseProgress?.courseVersionId ?? course?.defaultCourseVersionId ?? null;

  // Compute navigation state
  const navigationItems = useModuleNavigation({
    modules: modules ?? [],
    courseProgress,
    lockingDisabled,
  });

  // Initialize player state
  const {
    isSidebarOpen,
    currentModule,
    goToModule,
    toggleSidebar,
    closeSidebar,
    updateModuleCompletion,
  } = useCoursePlayer({
    courseId,
    currentModuleId: moduleId,
    modules: navigationItems,
    hasInternalNav,
  });

  const shouldRenderSidebar = computeShouldRenderSidebar({
    moduleNavigationEnabled: showModuleNavigation,
    isAdaptStructure,
    moduleCount: navigationItems.length,
  });
  const sidebarDisabledReason = !canInteractWithSidebar ? INTERNAL_NAVIGATION_REASON : undefined;
  const activeModule = currentModule;

  const handleModuleSelect = useCallback(
    (nextModuleId: string) => {
      if (!canInteractWithSidebar) {
        closeSidebar();
        return;
      }
      goToModule(nextModuleId);
    },
    [canInteractWithSidebar, closeSidebar, goToModule],
  );

  // Completion Modal State
  const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
  const [hasSeenCompletionModal, setHasSeenCompletionModal] = useState(false);
  const [isCertificateLoading, setIsCertificateLoading] = useState(false);

  // Evaluation Modal State
  const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
  const hasCompletedEvaluation = !!courseProgress?.evaluationCompletedAt;

  const handleStartEvaluation = () => {
    setIsCompletionModalOpen(false);
    setIsEvaluationModalOpen(true);
  };

  // Show completion modal when course is completed
  useEffect(() => {
    if (courseProgress?.status === PROGRESS_STATUS.COMPLETED && !hasSeenCompletionModal) {
      setIsCompletionModalOpen(true);
      setHasSeenCompletionModal(true);
      // A completed course drops out of the /courses Featured slot for this learner, and counts on its paths.
      void resetCatalogHighlights(queryClient);
      void invalidateLearningPathProgress(queryClient);
      // It may unlock the next course in an in-order path the learner has access to.
      void invalidateCourseDetails(queryClient);
    }
  }, [courseProgress?.status, hasSeenCompletionModal]);

  const handleDownloadCertificate = async () => {
    try {
      setIsCertificateLoading(true);
      const blob = await downloadCourseCertificate(courseId);

      const rawTitle = course?.title || courseId;
      const safeTitle =
        rawTitle
          .replace(/[<>:"/\\|?*\0]/g, "-")
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 100) || courseId;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificate-${safeTitle}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (_error) {
      // Error handling is done in the service, but we might catch re-thrown errors
      // or just ensure loading state is reset. The service calls toastError.
    } finally {
      setIsCertificateLoading(false);
    }
  };

  // Throttling state for SCORM progress updates
  const lastSentPercent = useRef<number>(0);
  const lastSentAt = useRef<number>(0);
  const pendingUpdate = useRef<{
    percentComplete: number;
    score?: number;
    lastScoIdentifier?: string;
  } | null>(null);
  const lastSentPayload = useRef<{
    percentComplete: number;
    score?: number;
    lastScoIdentifier?: string;
  } | null>(null);
  const updateTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightFlush = useRef<Promise<void> | null>(null);
  const forceCommitRef = useRef<(() => Promise<void>) | null>(null);
  const currentModuleIdRef = useRef(activeModule?.id);
  currentModuleIdRef.current = activeModule?.id;

  const clampPercentage = (value: number): number => {
    const clamped = Math.min(Math.max(value, 0), 100);
    return Number(clamped.toFixed(2));
  };

  const getAdaptiveProgress = (adaptSuspendData?: AdaptSuspendData | null): number | undefined => {
    if (!adaptSuspendData) {
      return undefined;
    }

    // Prefer the higher of completion vs progress - some Adapt packages track
    // viewed content (_progress) separately from completed content (_completionProgress)
    const completion = normalizeAdaptMetric(adaptSuspendData._completionProgress);
    const progress = normalizeAdaptMetric(adaptSuspendData._progress);

    if (completion !== undefined || progress !== undefined) {
      return Math.max(completion ?? 0, progress ?? 0);
    }

    const status =
      typeof adaptSuspendData._status === "string" ? adaptSuspendData._status.toLowerCase() : null;
    if (adaptSuspendData._isComplete || status === "completed" || status === "passed") {
      return 100;
    }

    return undefined;
  };

  const hasMeaningfulChange = useCallback(
    (
      next: { percentComplete: number; score?: number; lastScoIdentifier?: string },
      prev: { percentComplete: number; score?: number; lastScoIdentifier?: string } | null,
    ) => {
      if (!prev) return true;

      const percentChanged = Math.abs(next.percentComplete - prev.percentComplete) >= 0.5;

      const scoreChanged =
        next.score !== prev.score &&
        (next.score === undefined ||
          prev.score === undefined ||
          Math.abs((next.score ?? 0) - (prev.score ?? 0)) >= 0.5);

      const scoChanged = next.lastScoIdentifier !== prev.lastScoIdentifier;

      return percentChanged || scoreChanged || scoChanged;
    },
    [],
  );

  const flushPendingUpdate = useCallback(async () => {
    const moduleIdAtStart = currentModuleIdRef.current;

    while (inFlightFlush.current) {
      await inFlightFlush.current;
    }

    if (currentModuleIdRef.current !== moduleIdAtStart) return;

    const payload = pendingUpdate.current;
    if (!payload || !activeModule?.id) return;

    pendingUpdate.current = null;
    if (updateTimeout.current) {
      clearTimeout(updateTimeout.current);
      updateTimeout.current = null;
    }

    const promise = updateModuleCompletion(activeModule.id, payload).then(
      () => {
        if (currentModuleIdRef.current === moduleIdAtStart) {
          lastSentPercent.current = payload.percentComplete;
          lastSentAt.current = Date.now();
          lastSentPayload.current = payload;
        }
        inFlightFlush.current = null;
      },
      () => {
        inFlightFlush.current = null;
      },
    );

    inFlightFlush.current = promise;
    await promise;
  }, [activeModule?.id, updateModuleCompletion]);

  // Throttled progress update handler
  const handleThrottledProgress = useCallback(
    async (event: { percentComplete?: number; score?: number; lastScoIdentifier?: string }) => {
      if (!activeModule?.id || event.percentComplete === undefined) {
        return;
      }

      const payload = {
        percentComplete: clampPercentage(event.percentComplete),
        score:
          event.score !== undefined && Number.isFinite(event.score)
            ? clampPercentage(event.score)
            : undefined,
        lastScoIdentifier: event.lastScoIdentifier,
      };

      if (!hasMeaningfulChange(payload, lastSentPayload.current)) {
        if (!pendingUpdate.current || !hasMeaningfulChange(payload, pendingUpdate.current)) {
          return;
        }
      }

      const now = Date.now();
      const timeDelta = now - lastSentAt.current;
      const percentDelta = Math.abs(payload.percentComplete - lastSentPercent.current);

      // Check if we should send immediately
      const shouldSendNow = timeDelta > 2000 || percentDelta >= 2;

      if (shouldSendNow) {
        // Set pending and flush through the serialized helper
        pendingUpdate.current = payload;
        if (updateTimeout.current) {
          clearTimeout(updateTimeout.current);
          updateTimeout.current = null;
        }
        await flushPendingUpdate();
      } else {
        // Store the latest update
        pendingUpdate.current = {
          percentComplete: payload.percentComplete,
          score: payload.score,
          lastScoIdentifier: payload.lastScoIdentifier,
        };

        // Clear existing timeout and set a new one
        if (updateTimeout.current) {
          clearTimeout(updateTimeout.current);
        }

        updateTimeout.current = setTimeout(() => {
          flushPendingUpdate();
        }, 2000 - timeDelta);
      }
    },
    [activeModule?.id, flushPendingUpdate, hasMeaningfulChange],
  );

  // Clear pending throttled updates and reset throttle baseline when module changes or component unmounts
  useEffect(() => {
    return () => {
      if (updateTimeout.current) {
        clearTimeout(updateTimeout.current);
        updateTimeout.current = null;
      }
      pendingUpdate.current = null;
      lastSentPayload.current = null;
      // Reset throttle baseline variables so new module sends first update immediately
      lastSentPercent.current = 0;
      lastSentAt.current = 0;
    };
  }, [activeModule?.id]);

  const scormSession = useMemo<ScormSessionConfig | null>(() => {
    if (!activeModule?.id || !courseVersionId) {
      return null;
    }

    return {
      courseModuleId: activeModule.id,
      courseVersionId,
    };
  }, [courseVersionId, activeModule?.id]);

  useEffect(() => {
    if (!isAdaptStructure) {
      return;
    }

    const canonicalModuleId = navigationItems[0]?.id;
    if (canonicalModuleId && moduleId !== canonicalModuleId) {
      navigate({
        search: updateModuleSearch(canonicalModuleId),
        replace: true,
      });
    }
  }, [isAdaptStructure, navigationItems, moduleId, navigate]);

  // If no module is selected, default to the first available module
  if (!isAdaptStructure && !moduleId && navigationItems.length > 0) {
    const firstAvailable = navigationItems.find((m) => !m.isLocked);
    if (firstAvailable?.id) {
      navigate({
        search: updateModuleSearch(firstAvailable.id),
        replace: true,
      });
      return null;
    }
  }

  const handleExit = createHandleExit({
    flushPendingUpdate,
    forceCommitRef,
    queryClient,
    getGetCourseProgressQueryKey,
    getListUserEnrollmentsQueryKey,
    resetCatalogHighlights: () => resetCatalogHighlights(queryClient),
    invalidateLearningPathProgress: () => invalidateLearningPathProgress(queryClient),
    courseId,
    navigate,
  });

  const resolvePercentComplete = (
    event: ScormProgressEvent,
    adaptSuspendData?: AdaptSuspendData | null,
  ): number | undefined => {
    let percent: number | undefined;

    if (typeof event.progressMeasure === "number" && Number.isFinite(event.progressMeasure)) {
      percent = event.progressMeasure * 100;
    }

    if (percent === undefined && typeof event.scoreScaled === "number") {
      // Some packages only provide scaled score between 0-1
      percent = event.scoreScaled * 100;
    }

    if (percent === undefined) {
      const status = event.completionStatus?.toLowerCase();
      if (status === "completed" || status === "passed") {
        percent = 100;
      } else if (status === "not attempted") {
        percent = 0;
      }
    }

    if (percent === undefined) {
      percent = getAdaptiveProgress(adaptSuspendData);
    }

    if (percent === undefined) {
      return undefined;
    }

    return clampPercentage(percent);
  };

  const resolveScore = (
    event: ScormProgressEvent,
    adaptSuspendData?: AdaptSuspendData | null,
  ): number | undefined => {
    if (typeof event.scoreScaled === "number" && Number.isFinite(event.scoreScaled)) {
      return clampPercentage(event.scoreScaled * 100);
    }
    if (typeof event.scoreRaw === "number" && Number.isFinite(event.scoreRaw)) {
      return clampPercentage(event.scoreRaw);
    }
    if (adaptSuspendData) {
      const adaptScore = normalizeAdaptMetric(adaptSuspendData._score);
      if (adaptScore !== undefined) {
        return adaptScore;
      }
    }

    return undefined;
  };

  const normalizeCompletionScore = (score?: number | null): number | undefined => {
    if (score == null || !Number.isFinite(score)) {
      return undefined;
    }

    const value = score <= 1 ? score * 100 : score;
    return clampPercentage(value);
  };

  const resolveLastScoIdentifier = (
    event: ScormProgressEvent,
    adaptSuspendData?: AdaptSuspendData | null,
  ): string | undefined => {
    const location =
      event.runtime?.["cmi.core.lesson_location"] ??
      event.runtime?.["cmi.location"] ??
      event.runtime?.["cmi.launch_data"];

    const identifier =
      location ??
      (typeof adaptSuspendData?._lastCompletedComponentId === "string"
        ? adaptSuspendData._lastCompletedComponentId
        : "");

    if (identifier == null) {
      return undefined;
    }

    const trimmed = String(identifier).trim();
    if (!trimmed) {
      return undefined;
    }

    return trimmed.length > 255 ? trimmed.slice(0, 255) : trimmed;
  };

  return (
    <div className="flex h-screen flex-col">
      {/* Header */}
      <PlayerHeader
        courseTitle={course?.title ?? "Course"}
        onExit={handleExit}
        onToggleSidebar={shouldRenderSidebar ? toggleSidebar : undefined}
        customShareButton={<ShareCourseButton coursePlayerUrl={window.location.href} />}
        isSidebarOpen={isSidebarOpen}
      />

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        {shouldRenderSidebar && (
          <ModuleNavigationSidebar
            modules={navigationItems}
            currentModuleId={moduleId}
            isOpen={isSidebarOpen}
            onClose={closeSidebar}
            onModuleSelect={handleModuleSelect}
            isInteractive={canInteractWithSidebar}
            disabledReason={sidebarDisabledReason}
            isAdaptLike={isAdaptStructure}
            courseProgress={
              courseProgress
                ? {
                    percentComplete: toNum(courseProgress.percentComplete),
                    isCompleted: courseProgress.status === PROGRESS_STATUS.COMPLETED,
                  }
                : undefined
            }
          />
        )}

        {/* Player Area */}
        <main className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
          {activeModule?.activeScormPackageId &&
          activeModule.id &&
          courseVersionId &&
          scormSession ? (
            <ScormApiProvider
              key={activeModule.id}
              session={scormSession}
              onCompletion={async (event) => {
                const moduleIdAtStart = activeModule?.id;
                if (!moduleIdAtStart) return;

                const normalizedScore = normalizeCompletionScore(event.score);
                const lastScoIdentifier =
                  pendingUpdate.current?.lastScoIdentifier ??
                  lastSentPayload.current?.lastScoIdentifier;

                const alreadyReported =
                  lastSentPayload.current &&
                  lastSentPayload.current.percentComplete >= 100 &&
                  (normalizedScore === undefined ||
                    (lastSentPayload.current.score !== undefined &&
                      Math.abs(lastSentPayload.current.score - normalizedScore) < 0.5));

                if (alreadyReported) {
                  return;
                }

                if (updateTimeout.current) {
                  clearTimeout(updateTimeout.current);
                  updateTimeout.current = null;
                }
                pendingUpdate.current = null;

                // Wait for any in-flight throttled flush to complete before sending completion
                if (inFlightFlush.current) {
                  await inFlightFlush.current;
                }
                if (currentModuleIdRef.current !== moduleIdAtStart) return;

                await updateModuleCompletion(moduleIdAtStart, {
                  percentComplete: 100,
                  score: normalizedScore,
                  lastScoIdentifier,
                });
                if (currentModuleIdRef.current !== moduleIdAtStart) return;

                lastSentPercent.current = 100;
                lastSentAt.current = Date.now();
                lastSentPayload.current = {
                  percentComplete: 100,
                  score: normalizedScore,
                  lastScoIdentifier,
                };
              }}
              onProgress={(event) => {
                const adaptSuspendData = isAdaptStructure
                  ? parseAdaptSuspendData(event.runtime)
                  : null;
                let percentComplete = resolvePercentComplete(event, adaptSuspendData);
                const score = resolveScore(event, adaptSuspendData);
                const lastScoIdentifier = resolveLastScoIdentifier(event, adaptSuspendData);

                if (percentComplete === undefined) {
                  return;
                }

                // Adapt runtime treats <1% as "not attempted", so bump to 1% to mark course as started.
                if (isAdaptStructure && percentComplete > 0 && percentComplete < 1) {
                  percentComplete = 1;
                }

                handleThrottledProgress({
                  percentComplete,
                  score,
                  lastScoIdentifier,
                });
              }}
            >
              <ScormCommitBridge commitRef={forceCommitRef} />
              <div className="flex h-full min-h-0 w-full flex-1 flex-col p-4 pb-16">
                <ScormPlayer className="flex-1" />
              </div>
            </ScormApiProvider>
          ) : (
            <div className="flex flex-1 items-center justify-center p-8">
              <div className="text-center">
                <h2 className="text-arc-gray-900 mb-2 text-2xl font-semibold">
                  No content available
                </h2>
                <p className="text-arc-gray-600">
                  This module does not have any SCORM content configured yet.
                </p>
              </div>
            </div>
          )}

          {/* Course Progress Bar */}
          {courseProgress && (
            <div className="bg-arc-base-white border-arc-gray-200 fixed right-0 bottom-0 left-0 z-50 border-t px-4 py-2">
              <CourseProgressBar currentProgress={toNum(courseProgress.percentComplete)} />
            </div>
          )}
        </main>
      </div>

      <CompletionModal
        isOpen={isCompletionModalOpen}
        onClose={() => setIsCompletionModalOpen(false)}
        courseTitle={course?.title ?? "Course"}
        completionDate={
          courseProgress?.completedAt ? new Date(courseProgress.completedAt) : undefined
        }
        onDownloadCertificate={handleDownloadCertificate}
        isCertificateLoading={isCertificateLoading}
        hasCompletedEvaluation={hasCompletedEvaluation}
        onStartEvaluation={handleStartEvaluation}
      />

      <CourseEvaluationModal
        isOpen={isEvaluationModalOpen}
        onClose={() => setIsEvaluationModalOpen(false)}
        courseId={courseId}
        courseTitle={course?.title ?? "Course"}
      />
    </div>
  );
}
